import { Combobox, Transition } from "@headlessui/react";
import { Fragment, useMemo, useState } from "react";
import { FiCheck, FiChevronDown } from "react-icons/fi";

type SearchSelectOption = {
  id: number | string;
  name: string;
};

type SearchSelectProps = {
  label: string;
  options: SearchSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
};

const SearchSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder = "Selecione uma opção",
  required = false,
  error = false,
  errorMessage,
}: SearchSelectProps) => {
  const [query, setQuery] = useState("");
  const selectedOption = options.find((option) => String(option.id) === value) || null;
  const filteredOptions = useMemo(
    () =>
      query === ""
        ? options
        : options.filter((option) => option.name.toLowerCase().includes(query.toLowerCase())),
    [options, query],
  );

  return (
    <div className="flex w-full flex-col gap-1.5">
      <Combobox
        value={selectedOption}
        onChange={(option: SearchSelectOption | null) => {
          onChange(option ? String(option.id) : "");
          setQuery("");
        }}
      >
        <Combobox.Label className="flex items-center gap-1 text-sm font-bold leading-[170%] text-grey900">
          {label}
          {required && <span className="text-red-500">*</span>}
        </Combobox.Label>
        <div className="relative">
          <Combobox.Input
            className={`h-[56px] w-full rounded-full border bg-white py-2 pl-4 pr-12 text-base font-normal text-grey900 outline-none ${error ? "border-red-500 focus:border-red-500" : "border-grey200 focus:border-secondary300"}`}
            displayValue={(option: SearchSelectOption | null) => option?.name || ""}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
            required={required}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-full">
            <FiChevronDown size={20} className="text-grey500" />
          </Combobox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options className="absolute z-50 mt-2 max-h-60 w-full overflow-auto rounded-2xl bg-white p-2 text-base shadow-lg ring-1 ring-black/5 focus:outline-none">
              {filteredOptions.length === 0 && query !== "" ? (
                <li className="px-3 py-2 text-sm text-grey500">Nenhum consumidor encontrado.</li>
              ) : (
                filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option.id}
                    value={option}
                    className={({ active }) =>
                      `relative flex cursor-pointer items-center rounded-xl px-3 py-2 text-sm text-grey900 ${
                        active ? "bg-grey100" : ""
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span className={selected ? "font-semibold" : "font-normal"}>
                          {option.name}
                        </span>
                        {selected && <FiCheck className="ml-auto text-secondary500" size={18} />}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
      {errorMessage && <p className="ml-4 text-xs font-medium text-red-500">{errorMessage}</p>}
    </div>
  );
};

export default SearchSelect;
